
import { Component, Inject, Vue } from "vue-property-decorator";
import { Container } from "inversify";
import { OrganizationStore } from "../../../stores/organization/OrganizationStore";
import LoadingOverlay from "../../../../../common/src/components/LoadingOverlay.vue";
import { OrganizationItem } from "@gsx/common/src/types/http/dashboard/organization";
import { PaymentService } from "../../../../../landing/src/utility/payment/PaymentService";
import { NotificationStore } from "../../../../../common/src/stores/NotificationStore";
import { Api } from "../../../../../common/src/api/Api";
import { AuthStore } from "../../../../../common/src/stores/AuthStore";
import { hasOrganizationAccess, TreeType } from "@gsx/common";
import SubscriptionUpgrade from "./SubscriptionUpgrade.vue";
import BedrockOrgResearch from "./BedrockOrgResearch.vue";

const lastUrlSegment = (url: string): string => url.match(/[^\/]+(?=\/$|$)/)?.[0] ?? "UNKNOWN";

@Component({
    components: {
        BedrockOrgResearch,
        LoadingOverlay,
        SubscriptionUpgrade,
    },
    filters: { lastUrlSegment },
})
export default class TechnicalOverview extends Vue {
    @Inject("container") readonly container!: Container;
    readonly organizationStore: OrganizationStore = this.container.get(OrganizationStore);
    readonly notificationStore: NotificationStore = this.container.get(NotificationStore);
    readonly api: Api = this.container.get(Api);
    readonly paymentService: PaymentService = this.container.get(PaymentService);
    readonly authStore: AuthStore = this.container.get(AuthStore);
    convertedProductsTree = {};
    convertedServicesTree = {};
    convertedOperatingRegimeTree = {};
    convertedApplicationTree = {};

    lastUrlSegment = (url: string): string => url.match(/[^\/]+(?=\/$|$)/)?.[0] ?? "UNKNOWN";

    created() {
        if (this.organization) {
            this.convertedProductsTree = this.offering(this.organization.productTags);
            this.convertedServicesTree = this.offering(this.organization.serviceTags);
            this.convertedOperatingRegimeTree = this.offering(
                this.organization.operatingRegimeTags,
            );
            this.convertedApplicationTree = this.offering(this.organization.applicationTags);
        }
    }

    get organizationId(): string {
        return this.$route.params.organization;
    }

    get organization(): OrganizationItem | null {
        if (this.organizationStore.item) {
            if (
                this.organizationStore.item.assessmentParameters &&
                hasOrganizationAccess(
                    this.authStore.user,
                    "technical.technicalAssessment",
                    this.organizationId,
                )
            ) {
                if (
                    this.organizationStore.item.assessmentParameters.externalFactorsNegative &&
                    !this.organizationStore.item.assessmentParameters.externalFactorsNegative
                        .length &&
                    this.organizationStore.item.assessmentParameters.externalFactorsPositive &&
                    !this.organizationStore.item.assessmentParameters.externalFactorsPositive
                        .length &&
                    this.organizationStore.item.assessmentParameters.gameChangersNegative &&
                    !this.organizationStore.item.assessmentParameters.gameChangersNegative.length &&
                    this.organizationStore.item.assessmentParameters.gameChangersPositive &&
                    !this.organizationStore.item.assessmentParameters.gameChangersPositive.length &&
                    this.organizationStore.item.assessmentParameters.technicalRisksNegative &&
                    !this.organizationStore.item.assessmentParameters.technicalRisksNegative
                        .length &&
                    this.organizationStore.item.assessmentParameters.technicalRisksPositive &&
                    !this.organizationStore.item.assessmentParameters.technicalRisksPositive
                        .length &&
                    this.organizationStore.item.assessmentParameters.testingCapabilityNegative &&
                    !this.organizationStore.item.assessmentParameters.testingCapabilityNegative
                        .length &&
                    this.organizationStore.item.assessmentParameters.testingCapabilityPositive &&
                    !this.organizationStore.item.assessmentParameters.testingCapabilityPositive
                        .length &&
                    this.organizationStore.item.assessmentParameters.topAccelerantsNegative &&
                    !this.organizationStore.item.assessmentParameters.topAccelerantsNegative
                        .length &&
                    this.organizationStore.item.assessmentParameters.topAccelerantsPositive &&
                    !this.organizationStore.item.assessmentParameters.topAccelerantsPositive
                        .length &&
                    !this.organizationStore.item.assessmentParameters.capabilityGrowthRate &&
                    !this.organizationStore.item.assessmentParameters.demandGrowthPotential &&
                    !this.organizationStore.item.assessmentParameters.diversityOfOfferings &&
                    !this.organizationStore.item.assessmentParameters.technicalOverall &&
                    !this.organizationStore.item.assessmentParameters.technicalRiskProfile &&
                    !this.organizationStore.item.assessmentParameters.technologyMarketFit
                ) {
                    this.organizationStore.item.assessmentParameters = undefined;
                }
            } else if (
                !this.organizationStore.item.assessmentParameters &&
                !hasOrganizationAccess(
                    this.authStore.user,
                    "technical.technicalAssessment",
                    this.organizationId,
                )
            ) {
                this.organizationStore.item.assessmentParameters = {};
            }
        }

        return this.organizationStore.item;
    }

    get downloadedReports() {
        if (this.authStore.user && this.authStore.user.downloadedReports) {
            return this.authStore.user.downloadedReports.map((report) => report.reportId);
        }

        return [];
    }

    calculateProgressWidth(value: number | undefined) {
        if (!value || isNaN(value)) {
            value = 0;
        }

        return { width: `${value * 10}%` };
    }

    get serviceOfferings() {
        return this.getOfferings(this.convertedServicesTree);
    }

    get productOfferings() {
        return this.getOfferings(this.convertedProductsTree);
    }

    get operatingRegimeOfferings() {
        return this.getOfferings(this.convertedOperatingRegimeTree);
    }

    get applicationOfferings() {
        return this.getOfferings(this.convertedApplicationTree);
    }

    getOfferings(
        offeringObj: any,
        depth = 0,
    ): { name: string; depth: number; hasConnector: boolean }[] {
        let offeringList: { name: string; depth: number; hasConnector: boolean }[] = [];

        let hasConnector = true;
        for (const [offeringName, children] of Object.entries(offeringObj) as any) {
            offeringList.push({ name: offeringName, depth: depth, hasConnector: hasConnector });
            if (Object.keys(children).length) {
                let newDepth = depth + 1;
                offeringList = offeringList.concat(this.getOfferings(children, newDepth));
                hasConnector = false;
            } else {
                hasConnector = true;
            }
        }

        return offeringList;
    }

    offering(offeringList: string[] | undefined) {
        if (!this.organization || !offeringList) {
            return {};
        }

        const gather: any = {};
        for (const product of offeringList) {
            const fullProduct = product.split(":");

            let treePath = "";

            for (const partialProduct of fullProduct.filter(
                (name) =>
                    name.toLowerCase() !== TreeType.Product &&
                    name.toLowerCase() !== TreeType.Service &&
                    name.toLowerCase() !== TreeType.OperatingRegime &&
                    name.toLowerCase() !== TreeType.Applications,
            )) {
                treePath += `${treePath.length ? "." : ""}${partialProduct}`;
                if (!this.objectByString(gather, treePath)) {
                    this.objectSetByString(gather, treePath, {});
                }
            }
        }

        return gather;
    }
    // https://stackoverflow.com/questions/6491463/accessing-nested-javascript-objects-and-arrays-by-string-path
    objectByString(o: any, s: any) {
        s = s.replace(/\[(\w+)\]/g, ".$1"); // convert indexes to properties
        s = s.replace(/^\./, ""); // strip a leading dot
        var a = s.split(".");
        for (var i = 0, n = a.length; i < n; ++i) {
            var k = a[i];
            if (o === Object(o) && k in o) {
                o = o[k];
            } else {
                return;
            }
        }
        return o;
    }

    // https://stackoverflow.com/questions/18936915/dynamically-set-property-of-nested-object
    objectSetByString(o: any, path: string, value: any) {
        var schema = o; // a moving reference to internal objects within obj
        var pList = path.split(".");
        var len = pList.length;
        for (var i = 0; i < len - 1; i++) {
            var elem = pList[i];
            if (!schema[elem]) schema[elem] = {};
            schema = schema[elem];
        }

        schema[pList[len - 1]] = value;
    }

    organizationUrl(organizationId: string) {
        return `/dashboard/organizations/${organizationId}`;
    }
}
